import {
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Card,
  Input,
  Spin,
  Select,
  Tooltip,
} from 'antd';
import React, {useEffect, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
import {
  PlusOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import {StoryRepository} from './StoryRepository';
import {StoryService} from './StoryService';
import {
  Story,
  Media,
  MediaType,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/story/graphql/story.interface';
import {storyStorageRepo} from './StoryStorageRepository';
import {LevelsService} from '../Level/LevelsService';
import {LevelUI} from '../Level/LevelTypes';
import {GradesService} from '../Grade/GradesService';
import {GradeUI} from '../Grade/GradeTypes';

const {Title, Text} = Typography;
const {Option} = Select;

const storyRepo = new StoryRepository();
const storyService = new StoryService(storyRepo);
const levelsService = new LevelsService();
const gradesService = new GradesService();

const CoverImage = ({coverMedia}: {coverMedia: Media}) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isVideo = coverMedia?.type === MediaType.VIDEO;
  const isAnimation = coverMedia?.type === MediaType.ANIMATION;

  useEffect(() => {
    if (!coverMedia) {
      setLoading(false);
      return;
    }

    const urlToResolve = coverMedia.mediaUrl;
    if (!urlToResolve) {
      setLoading(false);
      return;
    }

    storyStorageRepo
      .resolveUrl(urlToResolve)
      .then(url => setSignedUrl(url))
      .catch(err => console.error('Error resolving cover image:', err))
      .finally(() => setLoading(false));
  }, [coverMedia, isVideo]);

  if (loading) {
    return (
      <div
        style={{
          width: '50px',
          height: '50px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Spin size="small" />
      </div>
    );
  }

  // Render video
  if (isVideo && signedUrl) {
    return (
      <div
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '8px',
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}>
        <video
          src={signedUrl}
          autoPlay
          loop
          style={{width: '100%', objectFit: 'contain', background: '#000'}}
        />
      </div>
    );
  }

  if (!signedUrl) {
    return (
      <div
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '8px',
          background: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #cbd5e1',
        }}>
        <Text type="secondary" style={{fontSize: '10px'}}>
          No Media
        </Text>
      </div>
    );
  }

  return (
    <div style={{position: 'relative', width: '50px', height: '50px'}}>
      <img
        src={signedUrl}
        alt="cover"
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '8px',
          objectFit: 'cover',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      />
      {isVideo && (
        <div
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            background: 'rgba(0,0,0,0.6)',
            borderRadius: '4px',
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <PlayCircleOutlined style={{color: '#fff', fontSize: '12px'}} />
        </div>
      )}
      {isAnimation && (
        <div
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            background: 'rgba(128,0,255,0.8)',
            borderRadius: '4px',
            padding: '1px 4px',
            fontSize: '8px',
            fontWeight: 'bold',
            color: '#fff',
          }}>
          GIF
        </div>
      )}
    </div>
  );
};

export const StoryTable = () => {
  const history = useHistory();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [selectedLevelId, setSelectedLevelId] = useState<string | undefined>(
    undefined,
  );
  const [selectedGradeId, setSelectedGradeId] = useState<string | undefined>(
    undefined,
  );
  const [levels, setLevels] = useState<LevelUI[]>([]);
  const [grades, setGrades] = useState<GradeUI[]>([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  useEffect(() => {
    fetchStories();
    fetchFilterOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFilterOptions = async () => {
    setFiltersLoading(true);
    try {
      const [lvls, grds] = await Promise.all([
        levelsService.getLevels(),
        gradesService.getGrades(),
      ]);
      setLevels(lvls || []);
      setGrades(grds || []);
    } catch (err) {
      console.error('Error loading filter options:', err);
    } finally {
      setFiltersLoading(false);
    }
  };

  const fetchStories = async (levelId?: string, gradeId?: string) => {
    try {
      setLoading(true);
      const filters: Record<string, string> = {};
      if (levelId) filters.levelId = levelId;
      if (gradeId) filters.gradeId = gradeId;

      const response = await storyService.getStories(
        {first: 100},
        Object.keys(filters).length > 0 ? filters : undefined,
      );
      if (response && response.edges) {
        setStories(response.edges);
      }
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLevelChange = (value: string | undefined) => {
    setSelectedLevelId(value);
    fetchStories(value, selectedGradeId);
  };

  const handleGradeChange = (value: string | undefined) => {
    setSelectedGradeId(value);
    fetchStories(selectedLevelId, value);
  };

  const getLevelName = (levelId: string) => {
    const level = levels.find(l => l._id === levelId);
    return level ? level.name : levelId;
  };

  const getGradeName = (gradeId: string) => {
    const grade = grades.find(g => g._id === gradeId);
    return grade ? grade.name : gradeId;
  };

  const filteredStories = stories.filter(
    story =>
      story.title.en.toLowerCase().includes(searchText.toLowerCase()) ||
      (story.title.ar && story.title.ar.includes(searchText)),
  );

  const columns = [
    {
      title: 'Cover',
      dataIndex: 'coverMedia',
      key: 'coverMedia',
      render: (coverMedia: Media) => <CoverImage coverMedia={coverMedia} />,
    },
    {
      title: 'Title',
      key: 'title',
      render: (record: Story) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{fontSize: '16px'}}>
            {record.title.en}
          </Text>
          {record.title.ar && (
            <Text type="secondary" style={{fontSize: '12px'}}>
              {record.title.ar}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Level',
      dataIndex: 'levelId',
      key: 'levelId',
      render: (levelId: string) =>
        levelId ? (
          <Tag
            color="geekblue"
            style={{borderRadius: '6px', fontWeight: 600, fontSize: '12px'}}>
            {getLevelName(levelId)}
          </Tag>
        ) : (
          <Tag color="default" style={{borderRadius: '6px', color: '#aaa'}}>
            —
          </Tag>
        ),
    },
    {
      title: 'Grade',
      dataIndex: 'gradeId',
      key: 'gradeId',
      render: (gradeId: string) =>
        gradeId ? (
          <Tag
            color="purple"
            style={{borderRadius: '6px', fontWeight: 600, fontSize: '12px'}}>
            {getGradeName(gradeId)}
          </Tag>
        ) : (
          <Tag color="default" style={{borderRadius: '6px', color: '#aaa'}}>
            —
          </Tag>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: Story) => (
        <Tag color={record.isPublished ? 'green' : 'orange'}>
          {record.isPublished ? 'Published' : 'Draft'}
        </Tag>
      ),
    },
    {
      title: 'Chapters',
      key: 'chapters',
      render: (record: Story) => record.chapters?.length || 0,
    },
    {
      title: 'is Freemium',
      dataIndex: 'isFreemium',
      key: 'isFreemium',
      render: (isFreemium: boolean) => (
        <Tag color={isFreemium ? 'green' : 'red'}>
          {isFreemium ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Order',
      dataIndex: 'storyOrder',
      key: 'storyOrder',
      render: (order: number) => <Text strong>#{order}</Text>,
    },
  ];

  return (
    <div
      style={{
        padding: '24px',
        background: '#f0f2f5',
        minHeight: '100vh',
      }}>
      <Card
        bordered={false}
        style={{
          borderRadius: '16px',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.07)',
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
        }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
          }}>
          <div>
            <Title level={2} style={{margin: 0, color: '#1a1c2e'}}>
              Story Library
            </Title>
            <Text type="secondary">
              Manage and create immersive educational stories
            </Text>
          </div>
          <Link to="/stories/create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              style={{
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                height: '45px',
                padding: '0 24px',
                fontWeight: 600,
              }}>
              New Story
            </Button>
          </Link>
        </div>

        {/* ── Filters row ── */}
        <div
          style={{
            marginBottom: '20px',
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
          <Input
            placeholder="Search stories by title..."
            prefix={<SearchOutlined style={{color: '#bfbfbf'}} />}
            size="large"
            allowClear
            onChange={e => setSearchText(e.target.value)}
            style={{
              borderRadius: '12px',
              maxWidth: '360px',
            }}
          />

          <Tooltip title="Filter by Level">
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{marginRight: 6}} />
                  All Levels
                </span>
              }
              allowClear
              size="large"
              style={{minWidth: 180, borderRadius: '12px'}}
              loading={filtersLoading}
              onChange={(val: string | undefined) => handleLevelChange(val)}
              value={selectedLevelId}>
              {levels.map(lvl => (
                <Option key={lvl._id} value={lvl._id}>
                  {lvl.name}
                </Option>
              ))}
            </Select>
          </Tooltip>

          <Tooltip title="Filter by Grade">
            <Select
              placeholder={
                <span>
                  <FilterOutlined style={{marginRight: 6}} />
                  All Grades
                </span>
              }
              allowClear
              size="large"
              style={{minWidth: 180, borderRadius: '12px'}}
              loading={filtersLoading}
              onChange={(val: string | undefined) => handleGradeChange(val)}
              value={selectedGradeId}>
              {grades.map(grd => (
                <Option key={grd._id} value={grd._id}>
                  {grd.name}
                </Option>
              ))}
            </Select>
          </Tooltip>
        </div>

        <Table
          columns={columns}
          dataSource={filteredStories}
          rowKey="_id"
          loading={loading}
          pagination={{pageSize: 10}}
          style={{
            borderRadius: '12px',
            overflow: 'hidden',
          }}
          rowClassName="premium-row"
          onRow={record => ({
            onClick: () => {
              history.push(`/stories/${record._id}`);
            },
          })}
        />
      </Card>

      <style>{`
        .premium-row {
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .premium-row:hover {
          background-color: rgba(102, 126, 234, 0.05) !important;
        }
        .ant-table-thead > tr > th {
          background: #fafafa;
          color: #1a1c2e;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};
